import React, { useState, useEffect } from "react";
import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, UserCheck, Target, ShoppingCart, BarChart3,
  LogOut, Menu, X, ChevronDown, Database, FolderOpen, Gift, Palette, Tag, ExternalLink,
} from "lucide-react";
import { LOGO_URL, ColorBar } from "@/components/brand";

const menuItems = [
  { label: "Dashboard", path: "/admin", icon: LayoutDashboard, end: true },
  { label: "Leads", path: "/admin/leads", icon: Target },
  { label: "Orders", path: "/admin/orders", icon: ShoppingCart },
  { label: "Customers", path: "/admin/customers", icon: UserCheck },
  { label: "Corporate Gifting", path: "/admin/corporate-gifting", icon: Gift },
  { label: "Staff", path: "/admin/staff", icon: Users },
  { label: "Reports", path: "/admin/reports", icon: BarChart3 },
];

const masterDataItems = [
  { label: "Categories", path: "/admin/master/categories", icon: Tag },
  { label: "Gifting Categories", path: "/admin/master/gifting-categories", icon: FolderOpen },
  { label: "Colors", path: "/admin/master/colors", icon: Palette },
];

const itemCls = ({ isActive }) =>
  `relative flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-[3px] text-[0.95rem] font-display font-medium transition-colors ${
    isActive
      ? "bg-paper/10 text-paper before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:bg-process-magenta"
      : "text-paper/60 hover:text-paper hover:bg-paper/5"
  }`;

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onMaster = location.pathname.startsWith("/admin/master");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [masterOpen, setMasterOpen] = useState(onMaster);

  useEffect(() => { if (onMaster) setMasterOpen(true); }, [onMaster]);
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const nav = (
    <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-0.5" aria-label="Admin">
      {menuItems.map(({ label, path, icon: Icon, end }) => (
        <NavLink key={path} to={path} end={end} className={itemCls}
          data-testid={`sidebar-${label.toLowerCase().replace(/\s/g, "-")}`}>
          <Icon size={18} aria-hidden="true" />
          {label}
        </NavLink>
      ))}

      <div className="pt-4 mt-4 border-t border-paper/10">
        <button type="button" onClick={() => setMasterOpen(!masterOpen)} aria-expanded={masterOpen}
          className="w-full flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-[3px] text-[0.95rem] font-display font-medium text-paper/60 hover:text-paper hover:bg-paper/5 transition-colors">
          <Database size={18} aria-hidden="true" />
          <span className="flex-1 text-left">Master data</span>
          <ChevronDown size={15} aria-hidden="true" className={`transition-transform ${masterOpen ? "rotate-180" : ""}`} />
        </button>
        {masterOpen && (
          <div className="mt-0.5 ml-6 pl-2 border-l border-paper/15 space-y-0.5">
            {masterDataItems.map(({ label, path, icon: Icon }) => (
              <NavLink key={path} to={path} className={itemCls}
                data-testid={`sidebar-${label.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon size={16} aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    </nav>
  );

  const account = (
    <div className="p-3 border-t border-paper/10">
      <div className="flex items-center gap-3 px-2 py-2">
        <div className="w-9 h-9 shrink-0 bg-process-yellow text-ink flex items-center justify-center font-display font-bold rounded-[3px]">
          {user?.name?.charAt(0) || "A"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-paper truncate">{user?.name || "Admin"}</p>
          <p className="text-xs text-paper/50 truncate">{user?.role || user?.email}</p>
        </div>
      </div>
      <div className="mt-1 grid grid-cols-2 gap-1">
        <Link to="/" target="_blank" className="flex items-center justify-center gap-1.5 py-2 text-sm text-paper/60 hover:text-paper hover:bg-paper/5 rounded-[3px]">
          <ExternalLink size={14} aria-hidden="true" /> Website
        </Link>
        <button type="button" onClick={handleLogout} data-testid="sidebar-logout"
          className="flex items-center justify-center gap-1.5 py-2 text-sm text-paper/60 hover:text-paper hover:bg-paper/5 rounded-[3px]">
          <LogOut size={14} aria-hidden="true" /> Sign out
        </button>
      </div>
    </div>
  );

  const brand = (
    <Link to="/admin" className="inline-flex items-center gap-3">
      <span className="bg-paper px-2.5 py-1.5 rounded-[3px]"><img src={LOGO_URL} alt="Print Xpress" className="h-6 w-auto" /></span>
      <span className="font-display font-semibold text-sm text-paper/60">Admin</span>
    </Link>
  );

  return (
    <div data-testid="admin-layout" className="admin min-h-screen bg-paper flex">
      <aside className="hidden lg:flex lg:flex-col w-64 bg-ink text-paper fixed inset-y-0 left-0 z-40">
        <ColorBar onInk className="!h-1.5" />
        <div className="h-[4.25rem] flex items-center px-5 border-b border-paper/10">{brand}</div>
        {nav}
        {account}
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-ink text-paper">
        <ColorBar onInk className="!h-1" />
        <div className="h-14 flex items-center justify-between px-4">
          {brand}
          <button type="button" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-mobile-toggle"
            aria-label={sidebarOpen ? "Close menu" : "Open menu"} className="p-2 -mr-2">
            {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {sidebarOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-ink/60 z-40" onClick={() => setSidebarOpen(false)} />
          <aside className="lg:hidden fixed top-[3.75rem] bottom-0 left-0 w-72 bg-ink text-paper z-50 flex flex-col border-t border-paper/10">
            {nav}
            {account}
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 lg:ml-64 pt-[3.75rem] lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
